import PageLayout from "@/components/feature/PageLayout";
import { useLang } from "@/lib/useLang";
import { ABOUT } from "@/data/about";
import imgBridge from "@/assets/images/bridge.jpg";
import PerformanceSection from "@/pages/home/components/PerformanceSection";
import PartnersSection from "@/pages/home/components/PartnersSection";

const SectionTitle = ({ children }: { children: string }) => (
  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">{children}</h2>
);

// 회사 개요 항목 아이콘 (순서 = about.ts overview)
const OVERVIEW_ICONS = [
  "ri-building-2-line",
  "ri-calendar-check-line",
  "ri-ship-2-line",
  "ri-map-pin-2-line",
  "ri-award-line",
  "ri-flask-line",
  "ri-file-list-3-line",
  "ri-route-line",
];

export default function AboutPage() {
  const lang = useLang();
  const a = ABOUT[lang];

  return (
    <PageLayout eyebrow={a.eyebrow} title={a.title} desc={a.desc} seoTitle={a.seoTitle} seoDescription={a.seoDescription}>
      <div className="space-y-20">
        {/* 비전 · 핵심가치 */}
        <section>
          <div className="rounded-lg overflow-hidden mb-8">
            <img src={imgBridge} alt={lang === "ko" ? "선박 브리지의 항해 · IT 장비" : "Navigation and IT equipment on a ship's bridge"} className="w-full h-56 md:h-80 object-cover" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-reveal data-reveal-stagger>
            {a.values.map((v) => (
              <div key={v.title} className="glow-card glow-card-light p-6 rounded-lg bg-background-100 border border-background-200">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-background-50 mb-4">
                  <i className={`${v.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2">{v.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-foreground-700 md:text-lg leading-relaxed">{a.valuesSummary}</p>
        </section>

        {/* 회사 개요 */}
        <section id="overview" data-reveal>
          <SectionTitle>{a.overviewTitle}</SectionTitle>
          <div className="rounded-xl border border-background-200 bg-background-100 p-2 md:p-3">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
              {a.overview.map((row, i) => (
                <div key={row.label} className="flex gap-4 items-start rounded-lg bg-background-50 border border-background-200 px-5 py-4">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${OVERVIEW_ICONS[i] ?? "ri-information-line"} text-lg`}></i>
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs font-semibold tracking-wide text-foreground-500 mb-1">{row.label}</dt>
                    <dd className="text-sm md:text-base font-medium text-foreground-900 leading-relaxed">{row.value}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* 주요 사업 */}
        <section>
          <SectionTitle>{a.businessTitle}</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-reveal data-reveal-stagger>
            {a.business.map((b) => (
              <div key={b.title} className="glow-card glow-card-light flex gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className={`${b.icon} text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-1">{b.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 조직 · 인력 */}
        <section id="organization">
          <SectionTitle>{a.orgTitle}</SectionTitle>
          <p className="text-foreground-700 leading-relaxed mb-6 max-w-3xl">{a.orgDesc}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-reveal data-reveal-stagger>
            {a.org.map((o) => (
              <div key={o.name} className="glow-card p-5 rounded-lg bg-foreground-950 text-background-50">
                <h3 className="font-heading font-bold mb-3 text-accent-300">{o.name}</h3>
                <ul className="space-y-1.5">
                  {o.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-foreground-300 leading-relaxed">
                      <span className="w-3 shrink-0 text-center text-accent-300">·</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">{a.staffTitle}</h3>
          <p className="text-foreground-700 leading-relaxed mb-6 max-w-3xl">{a.staffDesc}</p>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {a.staff.map((s) => (
                <div key={s.title} className="p-4 rounded-lg bg-background-100 border border-background-200">
                  <div className="font-semibold text-foreground-950 mb-1">{s.title}</div>
                  <div className="text-sm text-foreground-600">{s.desc}</div>
                </div>
              ))}
            </div>
            <ol className="lg:col-span-5 space-y-2">
              {a.tiers.map((tier) => (
                <li key={tier.grade} className="flex gap-3 p-3 rounded-lg border border-background-200 bg-background-50">
                  <span className="w-14 shrink-0 px-2 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-bold text-center h-fit">{tier.grade}</span>
                  <span className="text-sm text-foreground-700 leading-relaxed">{tier.role}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 사업 실적 · 파트너 (홈에서 이동) */}
        <PerformanceSection />
        <PartnersSection />

        {/* 연혁 */}
        <section id="history">
          <div data-reveal>
            <SectionTitle>{a.historyTitle}</SectionTitle>
          </div>
          <div className="relative pl-8 md:pl-12">
            <div className="absolute left-2 md:left-4 top-2 bottom-2 w-px bg-background-300"></div>
            <div className="space-y-8" data-reveal data-reveal-stagger>
              {a.history.map((h, i) => (
                <div key={h.year} className="relative">
                  <div className={`absolute -left-8 md:-left-12 top-1.5 w-4 h-4 rounded-full border-4 ${i === 0 ? "bg-accent-500 border-accent-200" : "bg-background-50 border-primary-500"}`}></div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-2">{h.year}</div>
                  <ul className="space-y-1">
                    {h.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm md:text-base text-foreground-700 leading-relaxed">
                        <span className="w-3 shrink-0 text-center text-primary-500">·</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
